<template>
  <div class="container">
    <h1 class="subs-title">Promo Codes</h1>
    <h5 class="info-text">
      The discount can either be a percentage or a specific amount. To create a code for free
      entrance, choose the Discount percentage type and input 100% in the Discount percentage field.
    </h5>
    <div class="create-search-container">
      <main-button
        style="max-width: 244px; margin-bottom: 1rem"
        @click="openCreateModal = !openCreateModal"
      >
        <span class="add-symbol">+</span>Create promo code
      </main-button>
      <v-text-field
        v-if="!$apollo.loading"
        label="Search Promo Code by Name"
        append-icon="search"
        box
        style="max-width: 24.063rem"
        @change="handleSearchChange($event)"
      />
    </div>
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <div v-if="!$apollo.loading && promoCodes.length" class="attendee_table">
      <selected-items-header
        v-if="selected.length"
        :selected="selected"
        name="promo code"
        @clear="selected = []"
        @delete="showDeleteDialog = true"
      />
      <table-list
        :selected.sync="selected"
        :organization-slug="organizationSlug"
        :attendees="promoCodes"
        body-style="grid-auto-columns: 50px minmax(50px, 150px) !important;"
        :visible-fields="visibleFields"
        :attendee-fields="attendeeFields"
        :pagination="pagination"
        @click="handlePromoCodeClick($event)"
        @sort="handleSort($event)"
      />
      <table-pagination
        :pagination="pagination"
        :total-attendees="totalPromoCodesCount"
        @change="handlePagination($event)"
        @changePerPage="handlePagination($event)"
      />
    </div>
    <delete-dialog
      :content="`Are you sure you want to delete selected promo codes? This operation cannot be undone.`"
      :on-delete="deleteSelected"
      :show.sync="showDeleteDialog"
      title="Delete promo codes"
    />
    <create-promo-code-modal
      :show.sync="openCreateModal"
      :initial-values="form"
      :all-events="allEvents"
      :performances="performances"
      :order-items="orderItems"
      :vs="$v"
      @update-form="handleUpdateForm($event)"
      @create-promo-code="handleCreatePromoCode"
    />
    <simple-snackbar v-bind.sync="snackbar" />
  </div>
</template>

<script>
import { compact, intersection, isEqual, uniq, uniqBy } from 'lodash'
import moment from 'moment'
import { maxValue, minValue, required, requiredIf } from 'vuelidate/lib/validators'
import selectedItemsHeader from '../../../components/tickets/selected-items-header.vue'
import { GET_EVENTS } from '../../../graphql/event.graphql'
import { GET_ORGANIZATION } from '../../../graphql/organization.graphql'
import {
  CREATE_PROMO_CODE,
  DELETE_PROMO_CODES,
  GET_PROMO_CODES,
} from '../../../graphql/promoCode.graphql'
import tableList from '../../accreditations/attendee-table/list.vue'
import tablePagination from '../../accreditations/attendee-table/pagination.vue'
import deleteDialog from '../../shared/dialogs/delete-dialog.vue'
import simpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import CreatePromoCodeModal from './CreatePromoCodeModal.vue'
import { OrganizationType } from '@/types'

const selectedPerformances = (eventTemplates, allPerformances) => (val, model) => {
  const selectedEventTemplatesIds = eventTemplates
    .filter(et => model.eventIds.includes(et.id))
    .map(et => et.id)
    .sort()

  const selectedPerformanceEventTemplatesIds = uniq(
    allPerformances.filter(p => val.includes(p.id)).map(p => p.eventTemplate.id),
  ).sort()

  return isEqual(selectedEventTemplatesIds, selectedPerformanceEventTemplatesIds)
}

const selectedOrderItems = (eventsWithoutTemplate, allPerformances) => (val, model) => {
  const allEvents = [...eventsWithoutTemplate, ...allPerformances]

  const selectedEventsIds = [
    ...model.eventIds.filter(ei => allEvents.map(e => e.id).includes(ei)),
    ...model.performanceIds,
  ].sort()

  const selectedOrderItemsEventsIds = allEvents
    .filter(e => e.orderItems.map(oi => oi.id).some(el => val.includes(el)))
    .map(e => e.id)
    .sort()

  return isEqual(selectedOrderItemsEventsIds, selectedEventsIds)
}

export default {
  name: 'promo-codes-container',
  components: {
    deleteDialog,
    tableList,
    tablePagination,
    selectedItemsHeader,
    CreatePromoCodeModal,
    simpleSnackbar,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    const { pagination } = this.getInitialFromQueryParams()

    return {
      promoCodes: [],
      pagination,
      showDeleteDialog: false,
      openCreateModal: false,
      openUpdateModal: false,
      selected: [],
      attendeeFields: {},
      visibleFields: ['Name', 'Code', 'Discount', 'Start date', 'End date'],
      totalPromoCodesCount: 0,
      form: {
        eventIds: [],
        performanceIds: [],
        orderItemIds: [],
      },
      allEvents: [],
      eventTemplates: [],
      eventsWithoutTemplate: [],
      allPerformances: [],
      snackbar: {},
      organization: {},
      searchTerm: null,
    }
  },
  validations() {
    return {
      form: {
        discountType: { required },
        name: { required },
        code: { required },
        quantityPerTicket: {
          minValue: minValue(0),
          required: requiredIf(({ unlimited }) => !unlimited),
        },
        unlimited: { required: requiredIf(({ quantityPerTicket }) => !quantityPerTicket) },
        ticketsPerOrder: { required, minValue: minValue(1), maxValue: maxValue(10) },
        discountPercentage: {
          required: requiredIf(({ discountType }) => discountType === 'PERCENTAGE'),
          maxValue: maxValue(100),
          minValue: minValue(0),
        },
        discountAmount: {
          required: requiredIf(({ discountType }) => discountType === 'AMOUNT'),
          minValue: minValue(0),
        },
        from: { required },
        to: { required },
        eventIds: { required },
        performanceIds: {
          required: requiredIf(
            val =>
              intersection(
                this.eventTemplates.map(et => et.id),
                val.eventIds,
              ).length,
          ),
          selectedPerformances: selectedPerformances(this.eventTemplates, this.allPerformances),
        },
        orderItemIds: {
          required,
          selectedOrderItems: selectedOrderItems(this.eventsWithoutTemplate, this.allPerformances),
        },
      },
    }
  },
  computed: {
    performances() {
      const newPerformances = this.allPerformances
        .filter(p => this.form.eventIds.includes(p.eventTemplate.id))
        .map(p => ({
          ...p,
          title: `${p.eventTemplate.title} - ${moment(p.from).format('DD/MM/YYYY. HH:mm')}`,
        }))

      if (newPerformances.length === 0) {
        this.$v.form.performanceIds.$reset()
      }

      return newPerformances
    },
    orderItems() {
      return compact(
        [...this.allEvents, ...this.allPerformances]
          .filter(e =>
            [
              ...this.form.eventIds,
              ...(this.form.performanceIds ? this.form.performanceIds : []),
            ].includes(e.id),
          )
          .flatMap(e =>
            e.orderItems
              ? e.orderItems.map(oi => ({
                  ...oi,
                  name: `${e.title}${
                    e.eventTemplate ? ` (${moment(e.from).format('DD/MM/YYYY. HH:mm')})` : ''
                  } - ${oi.name}`,
                }))
              : [],
          ),
      )
    },
  },
  apollo: {
    getPromoCodes: {
      query: GET_PROMO_CODES,
      manual: true,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          pagination: {
            ...this.pagination,
            per_page: this.pagination.per_page === 'All' ? -1 : this.pagination.per_page,
            sortBy: this.transformSortByString(this.pagination.sortBy),
          },
          searchTerm: this.searchTerm,
        }
      },
      result({ data: { getPromoCodes }, loading }) {
        if (!loading) {
          this.promoCodes = getPromoCodes.promoCodes.map(pc => ({
            ...pc,
            from: moment(pc.from).format('DD/MM/YYYY. HH:mm'),
            to: moment(pc.to).format('DD/MM/YYYY. HH:mm'),
            discount:
              pc.discountType === 'PERCENTAGE'
                ? pc.discountPercentage
                  ? `${pc.discountPercentage}%`
                  : '-'
                : pc.discountAmount
                ? `${pc.discountAmount} ${pc.currency}`
                : '-',
          }))
          this.totalPromoCodesCount = getPromoCodes.count
        }
      },
    },
    getEvents: {
      query: GET_EVENTS,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data: { getEvents }, loading }) {
        if (!loading) {
          const liveEvents = getEvents.filter(e => moment(e.to).isAfter(moment()))
          const eventsWithoutTemplate = liveEvents.filter(e => !e.eventTemplate)
          const eventTemplates = uniqBy(compact(liveEvents.map(e => e.eventTemplate)), 'id')

          this.allEvents = [...eventsWithoutTemplate, ...eventTemplates]
          this.eventsWithoutTemplate = eventsWithoutTemplate
          this.eventTemplates = eventTemplates
          this.allPerformances = liveEvents.filter(e => e.eventTemplate)
        }
      },
    },
    getOrganization: {
      query: GET_ORGANIZATION,
      variables() {
        return { organizationSlug: this.organizationSlug }
      },
      result({ data: { getOrganization }, loading }) {
        if (!loading) {
          this.organization = getOrganization
        }
      },
    },
  },
  methods: {
    async handlePromoCodeClick(promoCodeId) {
      if (this.organization.type === OrganizationType.THEATER) {
        this.goTo(this.routes.theaterOrganization.theaterOrganizationPromoCodeInfo, {
          promoCodeId: parseInt(promoCodeId),
        })
      } else if (this.organization.type === OrganizationType.SPORT_ORGANIZATION) {
        this.goTo(this.routes.sportOrganization.sportOrganizationPromoCodeInfo, {
          promoCodeId: parseInt(promoCodeId),
        })
      } else {
        this.goTo(this.routes.organization.organizationPromoCodeInfo, {
          promoCodeId: parseInt(promoCodeId),
        })
      }
    },
    handleUpdateForm(newForm) {
      this.form = { ...newForm }
    },
    async handleCreatePromoCode() {
      const { eventIds, performanceIds, orderItemIds, ...rest } = this.form

      const input = {
        ...rest,
        orderItemIds: orderItemIds.map(id => parseInt(id)),
        eventIds: [
          ...performanceIds,
          ...eventIds.filter(eventId => !this.eventTemplates.map(et => et.id).includes(eventId)),
        ].map(id => parseInt(id)),
      }

      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_PROMO_CODE,
        awaitRefetchQueries: true,
        refetchQueries: ['GetPromoCodes'],
        variables: {
          organizationSlug: this.organizationSlug,
          input,
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully created promo code!',
          color: 'success',
        }

        this.openCreateModal = false
        this.form = {
          eventIds: [],
          performanceIds: [],
          orderItemIds: [],
        }
      }
    },
    async deleteSelected() {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_PROMO_CODES,
        awaitRefetchQueries: true,
        refetchQueries: ['GetPromoCodes'],
        variables: {
          organizationSlug: this.organizationSlug,
          promoCodeIds: this.selected.map(id => parseInt(id)),
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully deleted promo codes!',
          color: 'success',
        }

        this.selected = []
      }
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalPromoCodesCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page,
        page,
      }

      this.addPaginationToRoute(this.pagination)
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination

      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      this.addPaginationToRoute(pagination)
      this.pagination = { ...pagination }
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 5,
          sortBy,
          descending: descending ? !!descending : false,
        },
      }
    },
    async addPaginationToRoute({ page, per_page, sortBy, descending }) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending: descending ? 1 : 0,
        },
      })
    },
    constructSoldAndInitialQuantities(orderItems) {
      return `${this.calculateTotalSoldQuantity(orderItems)}/${this.calculateTotalInitialQuantity(
        orderItems,
      )}`
    },
    calculateTotalSoldQuantity(orderItems) {
      return orderItems.reduce((sum, curr) => sum + curr.OrderItemPromoCode.soldQuantity, 0)
    },
    calculateTotalInitialQuantity(orderItems) {
      if (orderItems.some(item => item.OrderItemPromoCode.unlimited)) {
        return 'unlimited'
      }

      return orderItems.reduce((sum, curr) => sum + curr.OrderItemPromoCode.initialQuantity, 0)
    },
    transformSortByString(sortBy) {
      if (!sortBy) {
        return undefined
      }

      return sortBy === 'Start date'
        ? 'from'
        : sortBy === 'End date'
        ? 'to'
        : sortBy.slice(0, 1).toLowerCase() + sortBy.slice(1)
    },
    async handleSearchChange(input) {
      this.searchTerm = input
      await this.$apollo.queries.getPromoCodes.refetch()
    },
  },
}
</script>

<style scoped>
.info-text {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  color: #979797;
  margin-bottom: 1rem;
}
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #979797;
}
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
}
.subs-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
.create-search-container {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}
</style>
